import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { VERIFICATIONS_BASE, verificationKeys } from './verifications.query';
import type { VerificationLifecycleAction } from '@/client/types/verifications';

type VerificationActionVars = { vid: number; action: VerificationLifecycleAction; reason?: string };

// JSON bodies: `admin/verifications/*` is on the proxy's JSON allowlist
// (src/server/lib/utils.ts). Revoke is refused by the API without a reason.
export const useVerificationActionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation<unknown, Error, VerificationActionVars>({
    mutationFn: withErrorHandling(
      async ({ vid, action, reason }: VerificationActionVars) =>
        request<unknown>(`${VERIFICATIONS_BASE}/${vid}/${action}`, 'POST', {
          reason: reason?.trim() || undefined,
        }),
      'Could not update this verification',
    ),
    onSuccess: (_data, { vid }) => {
      qc.invalidateQueries({ queryKey: verificationKeys.lists() });
      qc.invalidateQueries({ queryKey: verificationKeys.detail(vid) });
    },
  });
};

type ItemReviewVars = { vid: number; vcid: number; approved: boolean; notes?: string };
type ItemWaiveVars = { vid: number; vcid: number; reason: string };
type ItemUnwaiveVars = { vid: number; vcid: number };

const checklistKey = (vid: number) => [...verificationKeys.detail(vid), 'checklist'];

/** Approve or reject ONE checklist item. Rejecting requires notes: the API
 *  refuses without them, because the applicant has to know what to fix. */
export const useChecklistItemReviewMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation<unknown, Error, ItemReviewVars>({
    mutationFn: withErrorHandling(
      async ({ vcid, approved, notes }: ItemReviewVars) =>
        request<unknown>(`${VERIFICATIONS_BASE}/checklist/${vcid}/review`, 'POST', {
          approved,
          notes: notes?.trim() || undefined,
        }),
      'Could not review this item',
    ),
    onSuccess: (_data, { vid }) => {
      qc.invalidateQueries({ queryKey: checklistKey(vid) });
      qc.invalidateQueries({ queryKey: verificationKeys.lists() });
    },
  });
};

export const useChecklistItemWaiveMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation<unknown, Error, ItemWaiveVars>({
    mutationFn: withErrorHandling(
      async ({ vcid, reason }: ItemWaiveVars) =>
        request<unknown>(`${VERIFICATIONS_BASE}/checklist/${vcid}/not-applicable`, 'POST', { reason }),
      'Could not waive this item',
    ),
    onSuccess: (_data, { vid }) => qc.invalidateQueries({ queryKey: checklistKey(vid) }),
  });
};

export const useChecklistItemUnwaiveMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation<unknown, Error, ItemUnwaiveVars>({
    mutationFn: withErrorHandling(
      async ({ vcid }: ItemUnwaiveVars) =>
        request<unknown>(`${VERIFICATIONS_BASE}/checklist/${vcid}/not-applicable`, 'DELETE'),
      'Could not undo the waiver',
    ),
    onSuccess: (_data, { vid }) => qc.invalidateQueries({ queryKey: checklistKey(vid) }),
  });
};
